



import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePlayerProfile } from '../../../features/playerProfile/playerProfileSlice';
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';
import { CARD, CardTitle, EditButton, InfoGrid, SECONDARY_BUTTON } from './profileCard';

const formatDOB = (dob) => {
  if (!dob) return '-';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '-';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const today = new Date();
  let age = today.getFullYear() - yyyy;
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return `${mm}/${dd}/${yyyy} (${age})`;
};

export const PlayerInfo = () => {
  const dispatch = useDispatch();
  const { profile, updateLoading } = useSelector((state) => state.playerProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [playerData, setPlayerData] = useState({
    dateOfBirth: '',
    class: '',
    bt: '',
    position: '',
    hometown: '',
    commitmentStatus: 'uncommitted',
    currentSchool: '',
    previousSchool: '',
    previousTeams: '',
    height: '',
    weight: '',
    ncaaId: '',
    phoneNumber: '',
    email: '',
    instaURL: '',
    xURL: ''
  });
  const [saveNotice, setSaveNotice] = useState('');

  // Normalize transfer status to match backend enum
  const normalizeTransferStatus = (status) => {
    if (!status) return 'Freshman';
    const statusMap = {
      'FRESHMAN': 'Freshman',
      'TRANSFER': 'Transfer',
      'JUCO TRANSFER': 'JUCO Transfer',
      '4-YEAR TRANSFER': '4-Year Transfer',
    };
    return statusMap[String(status).toUpperCase()] || status;
  };

  // Normalize player class to match backend enum
  const normalizePlayerClass = (playerClass) => {
    if (!playerClass) return 'Freshman';
    const classMap = {
      'FRESHMAN': 'Freshman',
      'FR': 'Freshman',
      'SOPHOMORE': 'Sophomore',
      'SO': 'Sophomore',
      'R-FRESHMAN': 'R-Freshman',
      'R-SOPHOMORE': 'R-Sophomore',
      'RED SHIRT': 'Red Shirt',
      'REDSHIRT': 'Red Shirt',
    };
    return classMap[String(playerClass).toUpperCase()] || playerClass;
  };

  const playerDataFromProfile = (p) => ({
    position: (p.primaryPosition || p.position) ? String(p.primaryPosition || p.position).toUpperCase() : '',
    dateOfBirth: (p.dob || p.dateOfBirth) ? String(p.dob || p.dateOfBirth) : '',
    class: p.playerClass ? normalizePlayerClass(p.playerClass) : '',
    bt: p.batsThrows ? String(p.batsThrows) : '',
    hometown: p.hometown ? String(p.hometown).toUpperCase() : '',
    commitmentStatus: p.commitmentStatus ? String(p.commitmentStatus).toLowerCase() : 'uncommitted',
    currentSchool: p.highSchool ? String(p.highSchool).toUpperCase() : '',
    previousSchool: p.previousSchool ? String(p.previousSchool).toUpperCase() : '',
    previousTeams: p.previousTeams ? String(p.previousTeams) : '',
    height: p.height ? String(p.height) : '',
    weight: p.weight ? `${p.weight} LBS` : '',
    ncaaId: p.ncaaId ? String(p.ncaaId) : '',
    phoneNumber: p.phoneNumber || '',
    email: p.email || '',
    instaURL: p.instaURL || '',
    xURL: p.xURL || ''
  });

  useEffect(() => {
    if (profile) {
      setPlayerData(playerDataFromProfile(profile));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const handleChange = (field, value) => {
    setPlayerData({ ...playerData, [field]: value });
  };

  const fieldClass = "w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-jp-ink text-sm placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition";
  const labelClass = "block text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-1";

  const commitmentLabel = playerData.commitmentStatus === 'committed' ? 'Committed' : 'Uncommitted';

  const handleSave = async () => {
    // Update all player info fields - send empty string if field is empty
    const updateData = {
      primaryPosition: playerData.position || '',
      playerClass: playerData.class ? normalizePlayerClass(playerData.class) : '',
      batsThrows: playerData.bt || '',
      hometown: playerData.hometown || '',
      commitmentStatus: playerData.commitmentStatus ? playerData.commitmentStatus.toLowerCase() : 'uncommitted',
      highSchool: playerData.currentSchool || '',
      previousSchool: playerData.previousSchool || '',
      previousTeams: playerData.previousTeams || '',
      height: playerData.height || '',
      weight: playerData.weight ? playerData.weight.replace(' LBS', '') : '',
      ncaaId: playerData.ncaaId || '',
      phoneNumber: playerData.phoneNumber.trim(),
      email: playerData.email.trim(),
      instaURL: playerData.instaURL.trim(),
      xURL: playerData.xURL.trim(),
    };

    setSaveNotice('');
    try {
      const saved = await dispatch(updatePlayerProfile(updateData)).unwrap();
      // The server may not store phone/email yet; say so rather than
      // letting the change quietly disappear.
      const unsaved = [
        (saved?.phoneNumber || '') !== updateData.phoneNumber && 'phone number',
        (saved?.email || '').toLowerCase() !== updateData.email.toLowerCase() && 'email',
      ].filter(Boolean);
      if (unsaved.length > 0) {
        setSaveNotice(`Your ${unsaved.join(' and ')} couldn't be saved yet. Everything else was saved.`);
      }
      setIsEditing(false);
    } catch (error) {
      setSaveNotice(typeof error === 'string' ? error : 'Failed to update profile.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveNotice('');
    if (profile) setPlayerData(playerDataFromProfile(profile));
  };

  const dropdown = (label, field, options) => (
    <div>
      <span className={labelClass}>{label}</span>
      <SimpleDropdown
        value={playerData[field]}
        onChange={(value) => handleChange(field, value)}
        options={options}
        placeholder="Select"
        variant="themed"
      />
    </div>
  );

  const textField = (label, field, id, type = 'text', placeholder) => (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <input id={id} type={type} value={playerData[field]} onChange={(e) => handleChange(field, e.target.value)} placeholder={placeholder} className={fieldClass} />
    </div>
  );

  return (
    <section className={CARD}>
      <CardTitle title="Player Information">
        {!isEditing && <EditButton onClick={() => { setSaveNotice(''); setIsEditing(true); }}>Edit profile</EditButton>}
      </CardTitle>

      {saveNotice && (
        <p role="status" className="mb-4 px-4 py-3 rounded-xl bg-amber-400/15 border border-amber-400/40 text-jp-ink text-sm">
          {saveNotice}
        </p>
      )}

      {!isEditing ? (
        <InfoGrid
          items={[
            { label: 'Position', value: playerData.position },
            { label: 'College', value: playerData.previousSchool },
            { label: 'Class', value: playerData.class ? String(playerData.class).toUpperCase() : '' },
            { label: 'B/T', value: playerData.bt },
            { label: 'Height', value: playerData.height },
            { label: 'Weight', value: playerData.weight },
            { label: 'Date of Birth', value: formatDOB(playerData.dateOfBirth) },
            { label: 'Hometown', value: playerData.hometown },
            { label: 'High School', value: playerData.currentSchool },
            { label: 'Commitment', value: commitmentLabel },
            { label: 'NCAA ID Number', value: playerData.ncaaId },
          ]}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {dropdown('Position', 'position', [
              { value: 'C', label: 'C (Catcher)' },
              { value: '1B', label: '1B (First Base)' },
              { value: '2B', label: '2B (Second Base)' },
              { value: '3B', label: '3B (Third Base)' },
              { value: 'SS', label: 'SS (Shortstop)' },
              { value: 'OF', label: 'OF (Outfield)' },
              { value: 'LF', label: 'LF (Left Field)' },
              { value: 'CF', label: 'CF (Center Field)' },
              { value: 'RF', label: 'RF (Right Field)' },
              { value: 'INF', label: 'INF (Infield)' },
              { value: 'P', label: 'P (Pitcher)' },
              { value: 'RHP', label: 'RHP (Right-Handed Pitcher)' },
              { value: 'LHP', label: 'LHP (Left-Handed Pitcher)' },
              { value: 'SP', label: 'SP (Starting Pitcher)' },
              { value: 'RP', label: 'RP (Relief Pitcher)' }
            ])}
            {dropdown('Class', 'class', [
              { value: 'Freshman', label: 'Freshman' },
              { value: 'Sophomore', label: 'Sophomore' },
              { value: 'R-Freshman', label: 'R-Freshman' },
              { value: 'R-Sophomore', label: 'R-Sophomore' },
              { value: 'Red Shirt', label: 'Red Shirt' }
            ])}
            {dropdown('B/T', 'bt', [
              { value: 'R/R', label: 'R/R' },
              { value: 'R/L', label: 'R/L' },
              { value: 'L/R', label: 'L/R' },
              { value: 'L/L', label: 'L/L' },
              { value: 'S/R', label: 'S/R' },
              { value: 'S/L', label: 'S/L' }
            ])}
            {dropdown('Commitment', 'commitmentStatus', [
              { value: 'uncommitted', label: 'Uncommitted' },
              { value: 'committed', label: 'Committed' }
            ])}
            {textField('Hometown', 'hometown', 'player-hometown')}
            {textField('High School', 'currentSchool', 'player-high-school')}
            {textField('NCAA ID Number', 'ncaaId', 'player-ncaa-id')}
          </div>
          <h3 className="mt-6 mb-3 text-sm font-extrabold text-jp-ink">Contact &amp; Social</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {textField('Phone', 'phoneNumber', 'player-phone', 'tel', '(555) 555-0123')}
            {textField('Email', 'email', 'player-email', 'email', 'name@email.com')}
            {textField('Instagram Link', 'instaURL', 'player-instagram', 'url', 'https://instagram.com/yourname')}
            {textField('X Link', 'xURL', 'player-x', 'url', 'https://x.com/yourname')}
          </div>
          <p className="mt-4 text-jp-muted text-xs">
            College, height, weight and date of birth can't be changed here.
          </p>
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" onClick={handleCancel} className={SECONDARY_BUTTON}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateLoading}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 text-sm font-bold uppercase tracking-wider hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {updateLoading && (
                <div className="w-4 h-4 mr-2 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
              )}
              Save changes
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default PlayerInfo;

